/**
 * Token Test Utilities
 * Helper functions for testing token expiration scenarios
 */

import { STORAGE_KEYS } from './localStorage'
import logger from './logger'

/**
 * Set a token with a specific expiration time (for testing)
 * @param {string} token - The access token
 * @param {Date|string} expiresAt - Expiration time (Date object or ISO string)
 */
export const setTestToken = (token, expiresAt) => {
    const expiresAtString = expiresAt instanceof Date ? expiresAt.toISOString() : expiresAt
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token)
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, expiresAtString)
    logger.debug('Test token set:', { token, expiresAt: expiresAtString })
}

/**
 * Set a token that expires in a specific number of seconds (for testing)
 * @param {string} token - The access token
 * @param {number} secondsFromNow - Seconds until expiration
 */
export const setExpiringToken = (token, secondsFromNow = 5) => {
    const expiresAt = new Date(Date.now() + secondsFromNow * 1000)
    setTestToken(token, expiresAt)
    logger.debug(`Test token set to expire in ${secondsFromNow} seconds`)
}

/**
 * Set an already expired token (for testing)
 * @param {string} token - The access token
 * @param {number} secondsAgo - How many seconds ago it expired (default: 60)
 */
export const setExpiredToken = (token, secondsAgo = 60) => {
    const expiresAt = new Date(Date.now() - secondsAgo * 1000)
    setTestToken(token, expiresAt)
    logger.debug(`Test token set to expired ${secondsAgo} seconds ago`)
}

/**
 * Clear test tokens
 */
export const clearTestToken = () => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT)
    logger.debug('Test tokens cleared')
}

/**
 * Get current token info for debugging
 */
export const getTokenInfo = () => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
    const expiresAt = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT)

    if (!token || !expiresAt) {
        return { hasToken: false, token: null, expiresAt: null, isExpired: null }
    }

    const expiresDate = new Date(expiresAt)
    const isExpired = expiresDate <= new Date()
    const timeUntilExpiry = expiresDate.getTime() - Date.now()

    return {
        hasToken: true,
        token: token.substring(0, 20) + '...', // Show only first 20 chars for security
        expiresAt: expiresAt,
        isExpired,
        timeUntilExpiry: Math.round(timeUntilExpiry / 1000), // seconds
        expiresIn: isExpired ? 'EXPIRED' : `${Math.round(timeUntilExpiry / 1000)}s`,
    }
}

// Make functions available in browser console for testing
if (typeof window !== 'undefined' && process.env.REACT_APP_ENV === 'dev') {
    window.tokenTestUtils = {
        setTestToken,
        setExpiringToken,
        setExpiredToken,
        clearTestToken,
        getTokenInfo,
    }
    logger.debug('Token test utilities available in window.tokenTestUtils')
}
